import { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  CheckCircleIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { TFSLoader } from '../components/ui';
import { RefreshButton } from '../components/common';
import { freightAPI } from '../api';

const Analytics = () => {
  const [dateRange, setDateRange] = useState('7');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);

  // Fetch on mount and when date range changes
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const data = await freightAPI.getAnalytics(parseInt(dateRange));
        setAnalyticsData(data);
        setLastRefreshTime(new Date());
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [dateRange]);

  // Manual refresh function
  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      const data = await freightAPI.getAnalytics(parseInt(dateRange));
      setAnalyticsData(data);
      setLastRefreshTime(new Date());
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Process analytics data
  const stats = analyticsData ? {
    total: analyticsData.total_requests || 0,
    pending: analyticsData.pending_requests || 0,
    completed: analyticsData.completed_requests || 0,
    completionRate: analyticsData.completion_rate?.toFixed(1) || '0.0'
  } : { total: 0, pending: 0, completed: 0, completionRate: '0.0' };

  const dailyCounts = analyticsData?.daily_requests?.map(day => ({
    ...day,
    formattedDate: format(new Date(day.date), 'MMM dd')
  })) || [];

  const topSuppliers = analyticsData?.top_suppliers || [];
  
  const maxDailyCount = Math.max(...dailyCounts.map(d => d.total || 0), 1);
  const maxSupplierCount = Math.max(...topSuppliers.map(s => s.count || 0), 1);

  if (loading) {
    return <TFSLoader />;
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
            <p className="text-sm sm:text-base text-gray-600">Track performance and insights for freight requests</p>
            {lastRefreshTime && (
              <p className="text-xs text-gray-500 mt-1">
                Last updated: {format(lastRefreshTime, 'MMM dd, yyyy HH:mm:ss')}
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-5 w-5 text-gray-400" />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-2 sm:px-3 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="7">Last 7 days</option>
                <option value="14">Last 14 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
            </div>
            <RefreshButton
              onClick={handleRefresh}
              isRefreshing={isRefreshing}
              loading={loading}
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 card-hover">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Requests</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{stats.total}</p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-blue-50">
              <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 card-hover">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completion Rate</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{stats.completionRate}%</p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-green-50">
              <ArrowTrendingUpIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 card-hover">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Requests</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{stats.pending}</p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-yellow-50">
              <ClockIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 card-hover">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{stats.completed}</p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-green-50">
              <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Daily Request Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Daily Requests</h2>
            <ChartBarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
          </div>
          <div className="space-y-2 sm:space-y-3">
            {dailyCounts.map((day) => (
              <div key={day.date} className="flex items-center">
                <div className="w-12 sm:w-16 text-xs text-gray-500">
                  {day.formattedDate}
                </div>
                <div className="flex-1 mx-2 sm:mx-3">
                  <div className="flex items-center space-x-1">
                    <div
                      className="bg-indigo-500 rounded-sm h-5 sm:h-6 flex items-center justify-center text-white text-xs font-medium"
                      style={{ width: `${(day.total / maxDailyCount) * 100}%`, minWidth: day.total > 0 ? '24px' : '0' }}
                    >
                      {day.total > 0 && day.total}
                    </div>
                  </div>
                </div>
                <div className="w-6 sm:w-8 text-xs text-gray-500 text-right">
                  {day.total}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Suppliers */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Top Suppliers</h2>
            <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full">AI Classified</span>
          </div>
          {topSuppliers.length > 0 ? (
            <div className="space-y-2 sm:space-y-3">
              {topSuppliers.map((supplier) => (
                <div key={supplier.name} className="flex items-center">
                  <div className="w-24 sm:w-32 text-xs text-gray-700 truncate" title={supplier.name}>
                    {supplier.name}
                  </div>
                  <div className="flex-1 mx-2 sm:mx-3">
                    <div className="flex items-center space-x-1">
                      <div
                        className="bg-purple-500 rounded-sm h-5 sm:h-6 flex items-center justify-center text-white text-xs font-medium"
                        style={{ width: `${(supplier.count / maxSupplierCount) * 100}%`, minWidth: supplier.count > 0 ? '24px' : '0' }}
                      >
                        {supplier.count}
                      </div>
                    </div>
                  </div>
                  <div className="w-6 sm:w-8 text-xs text-gray-500 text-right">
                    {supplier.count}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 text-sm">
              No supplier data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;